import { useEffect } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';

import {
  clearChunkErrorRecoveryCounter,
  useChunkErrorRecovery,
} from '@/components/error-handling/hooks/use-chunk-error-recovery';
import { Button } from '@/components/ui/button';
import { trackError } from '@/lib/monitoring/track-error';
import * as Logger from '@/lib/monitoring/logger';

export default function RouteErrorBoundary() {
  const error = useRouteError();

  const isChunkError = useChunkErrorRecovery(error);

  // Reset the shared retry budget once we render a non-chunk route, so the
  // next future chunk-load failure starts fresh.
  useEffect(() => {
    if (!isChunkError) clearChunkErrorRecoveryCounter();
  }, [isChunkError]);

  // Log and track non-chunk errors
  useEffect(() => {
    if (isChunkError) return;

    if (error instanceof Error) {
      Logger.error('Routing Error Caught:', error, { context: 'RouteErrorBoundary', url: window.location.href });
      trackError(error, {
        component: 'RouteErrorBoundary',
        action: 'route-error',
        metadata: { url: window.location.href },
      });
    } else {
      Logger.error('Routing Error Caught:', null, { context: 'RouteErrorBoundary', error: String(error), url: window.location.href });
    }
  }, [error, isChunkError]);

  // Standard error handling
  let status = 500;
  let message = 'An unexpected error occurred.';
  let stack = '';

  if (isRouteErrorResponse(error)) {
    status = error.status;
    message = error.statusText || error.data?.message || 'An error occurred.';
    if (status === 404) {
      message = 'Sorry, the page you\'re looking for doesn\'t exist.';
    } else if (status === 401) {
      message = 'Sorry, you aren\'t authorized to see this.';
    } else if (status === 503) {
      message = 'Looks like our API is down.';
    }
  } else if (error instanceof Error) {
    message = error.message;
    stack = error.stack || '';
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
      <div className="w-full max-w-(--breakpoint-md) rounded-xl border border-border bg-card p-8 shadow-card-elevated">
        <div className="mb-6 flex justify-center">
          <div className="relative">
            <div className="absolute -inset-0.5 rounded-full bg-linear-to-r from-pink-600 to-purple-600 opacity-75 blur"></div>
              <div className="relative rounded-full bg-background p-2">
              <svg
                className="h-16 w-16 text-destructive"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
            </div>
          </div>
        </div>

        <h1 className="mb-2 text-3xl font-bold text-foreground">Oops! Error {status}</h1>
        <p className="mb-6 text-lg text-foreground/90">{message}</p>

        {import.meta.env.DEV && stack && (
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">Stack Trace</p>
              <span className="rounded-full bg-status-warning/10 px-2.5 py-0.5 text-xs font-medium text-status-warning">
                Development Mode Only
              </span>
            </div>
            <pre className="max-h-64 overflow-auto rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-left text-xs text-foreground">
              {stack}
            </pre>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            asChild
            className="bg-linear-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700"
          >
            <Link to="/">Return Home</Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}
